import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Plus, Trash2, ImagePlus, Package } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import devApi from "../devApi";
import { API } from "../appApi";
import { Skeleton, spring } from "../bits";
import { DevCard, Tgl, Field, DevInput, DevTextarea, SaveBtn } from "./bits";

const PRODUCT_KEYS = ["name_gr", "name_en", "description_gr", "description_en", "price", "brand", "category_id", "supplier_id", "sizes", "colors", "custom_axis_name_gr", "custom_axis_name_en", "custom_axis_options", "variants", "active", "online_orderable"];

const emptyProduct = {
  id: null, name_gr: "", name_en: "", description_gr: "", description_en: "", price: "", brand: "",
  category_id: null, supplier_id: null, sizes: [], colors: [], custom_axis_name_gr: "", custom_axis_name_en: "",
  custom_axis_options: [], variants: [], active: true, online_orderable: true, images: [],
};

const newVariant = () => ({ id: crypto.randomUUID(), label: "", size: "", color: "", custom: "", stock: 0, availability: "in_stock", lead_days: null, price: null, sale_price: null });

const variantTitle = (v, colorName) => [v.size, colorName || v.color_name, v.custom].filter(Boolean).join(" · ") || "—";

// Full native product editor — basics, axes, variants (stock/availability/prices), photos
const ProductEditor = ({ product, onClose, onSaved }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [p, setP] = useState(() => ({ ...emptyProduct, ...(product || {}) }));
  const [cats, setCats] = useState([]);
  const [colorLib, setColorLib] = useState([]);
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    devApi.get("/shop/categories").then((r) => setCats(r.data)).catch(() => {});
    devApi.get("/shop/colors").then((r) => setColorLib(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!p.name_gr.trim() || p.price === "" || isNaN(Number(p.price))) {
      toast.error(`${m.pe_name_gr} + ${m.pe_price} *`);
      return null;
    }
    setBusy(true);
    try {
      const body = {};
      PRODUCT_KEYS.forEach((k) => { if (p[k] !== undefined) body[k] = p[k]; });
      body.price = Number(p.price);
      const res = p.id
        ? await devApi.put(`/admin/shop/products/${p.id}`, body)
        : await devApi.post("/admin/shop/products", body);
      const saved = res.data || { ...p };
      if (!p.id) setP((prev) => ({ ...prev, id: saved.id, images: saved.images || [] }));
      toast.success(m.dev_saved);
      return saved.id || p.id;
    } catch (err) {
      toast.error(err.response?.data?.detail || m.error_generic);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveAndClose = async () => {
    const id = await save();
    if (id) onSaved();
  };

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !p.id) return;
    setImgBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await devApi.post(`/admin/shop/products/${p.id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const fresh = await devApi.get(`/admin/shop/products/${p.id}`);
      setP((prev) => ({ ...prev, images: fresh.data.images || [] }));
      toast.success(m.dev_saved);
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
    finally { setImgBusy(false); e.target.value = ""; }
  };

  const deleteImage = async (idx) => {
    try {
      await devApi.delete(`/admin/shop/products/${p.id}/images/${idx}`);
      setP((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
  };

  const setVariant = (idx, patch) => {
    setP((prev) => {
      const variants = prev.variants.map((v, i) => (i === idx ? { ...v, ...patch } : v));
      return { ...prev, variants };
    });
  };

  const colorName = (cid) => (colorLib.find((c) => c.id === cid) || {}).name_gr || "";

  const AVAILS = [["in_stock", m.avail_in], ["on_order", m.avail_order], ["unavailable", m.avail_out]];

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-brand-cream overflow-y-auto" data-testid="product-editor">
      <div className="mx-auto max-w-md px-5 pt-6 pb-24">
        <div className="flex items-center gap-3 mb-5 sticky top-0 bg-brand-cream/95 backdrop-blur py-2 z-10">
          <motion.button whileTap={{ scale: 0.9 }} onClick={onClose} className="grid place-items-center h-10 w-10 rounded-full bg-white border border-brand/15 text-brand shrink-0" data-testid="pe-close" aria-label="close">
            <X className="h-5 w-5" />
          </motion.button>
          <h1 className="font-heading text-xl font-medium text-brand-ink truncate flex-1">{p.id ? m.pe_edit : m.pe_new}</h1>
          {p.id && (
            <button type="button" onClick={async () => { if (!window.confirm(m.pe_delete_confirm)) return; try { await devApi.delete(`/admin/shop/products/${p.id}`); toast.success(m.dev_saved); onSaved(); } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); } }} className="grid place-items-center h-10 w-10 rounded-full text-rose-400 hover:bg-rose-50 shrink-0" data-testid="pe-delete" aria-label="delete">
              <Trash2 className="h-5 w-5" />
            </button>
          )}
          <SaveBtn onClick={saveAndClose} busy={busy} label={m.dev_save} testid="pe-save" />
        </div>

        <div className="space-y-4">
          <DevCard>
            <p className="font-bold text-sm text-brand-ink mb-3">{m.pe_basics}</p>
            <div className="space-y-3">
              <Field label={`${m.pe_name_gr} *`}><DevInput value={p.name_gr} onChange={(e) => setP({ ...p, name_gr: e.target.value })} data-testid="pe-name-gr" /></Field>
              <Field label={m.pe_name_en}><DevInput value={p.name_en || ""} onChange={(e) => setP({ ...p, name_en: e.target.value })} data-testid="pe-name-en" /></Field>
              <Field label={m.pe_desc}><DevTextarea value={p.description_gr || ""} onChange={(e) => setP({ ...p, description_gr: e.target.value })} data-testid="pe-desc" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={`${m.pe_price} *`}><DevInput type="number" min="0" step="0.5" value={p.price} onChange={(e) => setP({ ...p, price: e.target.value })} data-testid="pe-price" /></Field>
                <Field label={m.pe_category}>
                  <select value={p.category_id || ""} onChange={(e) => setP({ ...p, category_id: e.target.value || null })} className="w-full h-11 rounded-xl border border-brand/20 bg-white px-3 text-sm text-brand-ink" data-testid="pe-category">
                    <option value="">—</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name_gr}</option>)}
                  </select>
                </Field>
              </div>
              <Field label={m.pe_brand}><DevInput value={p.brand || ""} onChange={(e) => setP({ ...p, brand: e.target.value })} data-testid="pe-brand" /></Field>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-brand-ink">{m.pe_active}</p>
                <Tgl on={!!p.active} onChange={(v) => setP({ ...p, active: v })} testid="pe-active" />
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-brand-ink">{m.pe_orderable}</p>
                <Tgl on={!!p.online_orderable} onChange={(v) => setP({ ...p, online_orderable: v })} testid="pe-orderable" />
              </div>
            </div>
          </DevCard>

          <DevCard>
            <p className="font-bold text-sm text-brand-ink mb-3">{m.pe_axes}</p>
            <div className="space-y-3">
              <Field label={m.pe_sizes}>
                <DevInput value={(p.sizes || []).join(", ")} onChange={(e) => setP({ ...p, sizes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder={m.comma_hint} data-testid="pe-sizes" />
              </Field>
              <Field label={m.pe_colors}>
                <div className="flex flex-wrap gap-1.5" data-testid="pe-colors">
                  {colorLib.map((c) => {
                    const on = (p.colors || []).includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setP({ ...p, colors: on ? p.colors.filter((x) => x !== c.id) : [...(p.colors || []), c.id] })}
                        className={`text-xs font-bold px-3 py-1.5 rounded-full border ${on ? "border-brand bg-brand text-brand-cream" : "border-brand/15 bg-white text-brand-ink/60"}`}
                        data-testid={`pe-color-${c.id}`}
                      >
                        {c.name_gr}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={m.pe_custom_axis}><DevInput value={p.custom_axis_name_gr || ""} onChange={(e) => setP({ ...p, custom_axis_name_gr: e.target.value })} data-testid="pe-custom-name" /></Field>
                <Field label={m.pe_custom_opts}>
                  <DevInput value={(p.custom_axis_options || []).join(", ")} onChange={(e) => setP({ ...p, custom_axis_options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder={m.comma_hint} data-testid="pe-custom-opts" />
                </Field>
              </div>
            </div>
          </DevCard>

          <DevCard>
            <div className="flex items-center justify-between mb-3">
              <p className="font-bold text-sm text-brand-ink">{m.pe_variants} ({(p.variants || []).length})</p>
              <button type="button" onClick={() => setP({ ...p, variants: [...(p.variants || []), newVariant()] })} className="inline-flex items-center gap-1 text-xs font-bold text-brand bg-brand-soft/60 rounded-full px-3 py-1.5" data-testid="pe-add-variant">
                <Plus className="h-3.5 w-3.5" /> {m.pe_add_variant}
              </button>
            </div>
            {(p.variants || []).length === 0 && <p className="text-xs text-brand-ink/40 italic">{m.dev_empty}</p>}
            <div className="space-y-3">
              {(p.variants || []).map((v, idx) => (
                <div key={v.id} className="rounded-xl border border-brand/10 bg-stone-alt/40 p-3" data-testid={`pe-variant-${idx}`}>
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-brand shrink-0" />
                    <p className="text-xs font-bold text-brand-ink flex-1 truncate">{variantTitle(v, colorName(v.color))}</p>
                    <button type="button" onClick={() => setP({ ...p, variants: p.variants.filter((_, i) => i !== idx) })} className="grid place-items-center h-7 w-7 rounded-full text-rose-400 hover:bg-rose-50" data-testid={`pe-variant-del-${idx}`} aria-label="delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {(p.sizes || []).length > 0 && (
                      <select value={v.size || ""} onChange={(e) => setVariant(idx, { size: e.target.value })} className="h-9 rounded-lg border border-brand/20 bg-white px-1.5 text-xs" data-testid={`pe-variant-size-${idx}`}>
                        <option value="">—</option>
                        {p.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                    {(p.colors || []).length > 0 && (
                      <select value={v.color || ""} onChange={(e) => setVariant(idx, { color: e.target.value })} className="h-9 rounded-lg border border-brand/20 bg-white px-1.5 text-xs" data-testid={`pe-variant-color-${idx}`}>
                        <option value="">—</option>
                        {p.colors.map((cid) => <option key={cid} value={cid}>{colorName(cid)}</option>)}
                      </select>
                    )}
                    {(p.custom_axis_options || []).length > 0 && (
                      <select value={v.custom || ""} onChange={(e) => setVariant(idx, { custom: e.target.value })} className="h-9 rounded-lg border border-brand/20 bg-white px-1.5 text-xs" data-testid={`pe-variant-custom-${idx}`}>
                        <option value="">—</option>
                        {p.custom_axis_options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    )}
                    <DevInput type="number" min="0" value={v.stock} onChange={(e) => setVariant(idx, { stock: Number(e.target.value) || 0 })} placeholder={m.pe_stock} className="!h-9 !text-xs" data-testid={`pe-variant-stock-${idx}`} />
                    <select value={v.availability} onChange={(e) => setVariant(idx, { availability: e.target.value })} className="h-9 rounded-lg border border-brand/20 bg-white px-1.5 text-xs col-span-2" data-testid={`pe-variant-avail-${idx}`}>
                      {AVAILS.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
                    </select>
                    <DevInput type="number" min="0" step="0.5" value={v.price ?? ""} onChange={(e) => setVariant(idx, { price: e.target.value === "" ? null : Number(e.target.value) })} placeholder={m.pe_price} className="!h-9 !text-xs" data-testid={`pe-variant-price-${idx}`} />
                    <DevInput type="number" min="0" step="0.5" value={v.sale_price ?? ""} onChange={(e) => setVariant(idx, { sale_price: e.target.value === "" ? null : Number(e.target.value) })} placeholder={m.dev_sale} className="!h-9 !text-xs" data-testid={`pe-variant-sale-${idx}`} />
                    <span />
                  </div>
                </div>
              ))}
            </div>
          </DevCard>

          <DevCard>
            <p className="font-bold text-sm text-brand-ink mb-3">{m.pe_photos}</p>
            {!p.id ? (
              <p className="text-xs text-brand-ink/40 italic">{m.pe_save_first}</p>
            ) : (
              <>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={uploadImages} data-testid="pe-file" />
                <div className="grid grid-cols-3 gap-2">
                  {(p.images || []).map((img, idx) => (
                    <div key={idx} className="relative rounded-xl overflow-hidden border border-brand/10" data-testid={`pe-img-${idx}`}>
                      <img src={`${API}/${img}`} alt="" className="w-full aspect-square object-cover" loading="lazy" />
                      <button type="button" onClick={() => deleteImage(idx)} className="absolute top-1 right-1 grid place-items-center h-7 w-7 rounded-full bg-black/55 text-white" data-testid={`pe-img-del-${idx}`} aria-label="delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button type="button" disabled={imgBusy} onClick={() => fileRef.current?.click()} className="aspect-square rounded-xl border-2 border-dashed border-brand/25 grid place-items-center text-brand disabled:opacity-40" data-testid="pe-upload">
                    {imgBusy ? <span className="text-[10px] font-bold">{m.dev_uploading}</span> : <ImagePlus className="h-6 w-6" />}
                  </button>
                </div>
              </>
            )}
          </DevCard>

          <SaveBtn onClick={saveAndClose} busy={busy} label={m.dev_save} testid="pe-save-bottom" />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductEditor;
